import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioAccommodationObject from '@/entities/ratioAccommodation/interface'
import ratioFamilyPackageObject from '@/entities/ratioFamilyPackage/interface'
import ratioAccommodationAddonObject from '@/entities/ratioAccommodationAddon/interface'
import travelPricingAgeGroupObject from '@/entities/travelPricingAgeGroup/interface'
import ratioTravelDateObject from '@/entities/ratioTravelDate/interface'

import {
  ALL_PRICING_RULES_QUERY,
  SINGLE_PRICING_RULES_QUERY,
  PRICING_RULES_DEPENDENTS_QUERY,
  CREATE_PRICING_RULES_MUTATION,
  UPDATE_PRICING_RULES_MUTATION,
  DELETE_PRICING_RULES_MUTATION,
  FORCE_DELETE_PRICING_RULES_MUTATION,
  ADD_PRICING_RULES_ACCOMMODATIONS_MUTATION,
  REMOVE_PRICING_RULES_ACCOMMODATIONS_MUTATION,
  CREATE_PRICING_RULES_ACCOMMODATIONS_MUTATION,
  DELETE_PRICING_RULES_ACCOMMODATIONS_MUTATION,
  ADD_PRICING_RULES_FAMILY_PACKAGES_MUTATION,
  REMOVE_PRICING_RULES_FAMILY_PACKAGES_MUTATION,
  CREATE_PRICING_RULES_FAMILY_PACKAGES_MUTATION,
  DELETE_PRICING_RULES_FAMILY_PACKAGES_MUTATION,
  ADD_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION,
  REMOVE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION,
  CREATE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION,
  DELETE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION,
  ADD_PRICING_RULES_AGE_GROUPS_MUTATION,
  REMOVE_PRICING_RULES_AGE_GROUPS_MUTATION,
  CREATE_PRICING_RULES_AGE_GROUPS_MUTATION,
  DELETE_PRICING_RULES_AGE_GROUPS_MUTATION,
  ADD_PRICING_RULES_TRAVEL_DATES_MUTATION,
  REMOVE_PRICING_RULES_TRAVEL_DATES_MUTATION,
  CREATE_PRICING_RULES_TRAVEL_DATES_MUTATION,
  DELETE_PRICING_RULES_TRAVEL_DATES_MUTATION,
  UPDATE_PRICING_RULES_TRAVEL_DATES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelPricingRulesRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PRICING_RULES_QUERY, variables).then((data: any) => {
      return hydrateObject(data.pricingRules)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PRICING_RULES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.pricingRules)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PRICING_RULES_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.pricingRules),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PRICING_RULES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.pricingRules),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PRICING_RULES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PRICING_RULES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PRICING_RULES_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pricingRules)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PRICING_RULES_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pricingRules)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_MUTATION, variables).then((data: any) => {
      return data.deleteTravelPricingRules
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PRICING_RULES_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelPricingRules
    })
  },

  assignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_RULES_ACCOMMODATIONS_MUTATION, variables)
  },

  unassignAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_RULES_ACCOMMODATIONS_MUTATION, variables)
  },

  createAccommodations: async (id: string, items: Array<ratioAccommodationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodations: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_RULES_ACCOMMODATIONS_MUTATION, variables)
  },

  deleteAccommodations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_ACCOMMODATIONS_MUTATION, variables)
  },

  assignFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_RULES_FAMILY_PACKAGES_MUTATION, variables)
  },

  unassignFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_RULES_FAMILY_PACKAGES_MUTATION, variables)
  },

  createFamilyPackages: async (id: string, items: Array<ratioFamilyPackageObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackages: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_RULES_FAMILY_PACKAGES_MUTATION, variables)
  },

  deleteFamilyPackages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        familyPackageIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_FAMILY_PACKAGES_MUTATION, variables)
  },

  assignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  unassignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  createAccommodationAddons: async (id: string, items: Array<ratioAccommodationAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddons: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  deleteAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  assignAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_RULES_AGE_GROUPS_MUTATION, variables)
  },

  unassignAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_RULES_AGE_GROUPS_MUTATION, variables)
  },

  createAgeGroups: async (id: string, items: Array<travelPricingAgeGroupObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroups: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_RULES_AGE_GROUPS_MUTATION, variables)
  },

  deleteAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_AGE_GROUPS_MUTATION, variables)
  },

  assignTravelDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelDateIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_RULES_TRAVEL_DATES_MUTATION, variables)
  },

  unassignTravelDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelDateIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_RULES_TRAVEL_DATES_MUTATION, variables)
  },

  createTravelDates: async (id: string, items: Array<ratioTravelDateObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelDates: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_RULES_TRAVEL_DATES_MUTATION, variables)
  },

  deleteTravelDates: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelDateIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_RULES_TRAVEL_DATES_MUTATION, variables)
  },

  updateTravelDatesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelDateIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_PRICING_RULES_TRAVEL_DATES_ORDER_MUTATION, variables)
  },
}

export default travelPricingRulesRepository
